import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed font-medium tracking-normal",
  {
    variants: {
      variant: {
        default:
          "bg-stone-950 text-white hover:bg-stone-800 disabled:bg-background disabled:text-stone-300",
        brand:
          "bg-green-800 text-white hover:bg-green-900 disabled:bg-green-900/40 disabled:text-white",
        outline:
          "border border-primary bg-white text-primary hover:bg-stone-100 disabled:border-primary disabled:bg-white disabled:text-stone-300",
        destructive:
          "bg-red-600 text-white hover:bg-red-700 disabled:bg-background disabled:text-stone-300",
        subtle:
          "bg-stone-100 text-stone-900 hover:bg-stone-200 disabled:bg-background disabled:text-stone-300",
        ghost:
          "bg-transparent text-stone-900 hover:bg-stone-100 disabled:bg-background disabled:text-stone-300",
        link: "bg-transparent text-stone-900 hover:underline hover:underline-offset-4 disabled:text-stone-300 disabled:underline disabled:underline-offset-4",
      },
      size: {
        sm: "h-8 px-[10px] py-[6px] text-sm leading-6",
        md: "h-10 px-4 py-[6px] text-sm leading-6",
        lg: "h-11 px-4 py-2 text-base leading-7",
        icon: "h-8 w-8",
      },
      rounded: {
        false: "rounded-md",
        true: "rounded-full",
      },
    },
    compoundVariants: [
      {
        variant: "link",
        className: "h-auto p-0",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "md",
      rounded: false,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
